import { useMutation, useQuery } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { AuthCard } from '@/components/AuthCard'
import { AuthCardBrand } from '@/components/AuthCardBrand'
import { AuthCardLead } from '@/components/AuthCardLead'
import { Button } from '@/components/Button'
import { Note } from '@/components/Note'
import { Alert } from '@/components/Alert'
import { getApiClient, ApiProblemError, type Problem } from '@/data/apiClient'
import { setAccessToken } from '@/data/store'
import { AUTH_BRAND, A_ORGBLOCKED_COPY } from '@/copy/registry'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import type { components } from '@/api/generated/schema'

type OrganisationMembership = components['schemas']['OrganisationMembership']

async function fetchOrganisations(): Promise<readonly OrganisationMembership[]> {
  const client = getApiClient()
  const { data, error } = await client.GET('/orgs', {})
  if (error) throw new ApiProblemError(error as Problem)
  return data.items as unknown as readonly OrganisationMembership[]
}

/**
 * `a-orgblocked` — a per-organisation, per-request refusal (§3.1i rule 1). Names no reason (rule
 * 1a — `access_block_note` is an internal ops column). `identityListOrganisations` "is also the
 * way out" — switching is a scope change, never re-authentication (rule 1, `LLD-IDN-077`).
 *
 * The real organisation switcher is shell chrome (§27.1) — Pass 3. This screen renders the list
 * it needs directly rather than waiting on that pass, since the list IS this screen's own stated
 * operation; a fuller switcher UI is that pass's to build, not reinvented here.
 */
export function AOrgblocked({
  orgName,
  onOrgSwitched,
  onSignedOut,
}: {
  orgName: string
  onOrgSwitched: () => void
  onSignedOut: () => void
}) {
  const orgs = useQuery({
    queryKey: ['organisations'],
    queryFn: fetchOrganisations,
  })

  const signOut = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const { error } = await client.POST('/auth/sign-out', { body: { global: false } })
      if (error) throw new ApiProblemError(error as Problem)
    },
    onSettled: () => {
      setAccessToken(null)
      onSignedOut()
    },
  })

  const reachable = orgs.data?.filter((o) => !o.access_blocked) ?? []
  const problem = orgs.error instanceof ApiProblemError ? orgs.error.problem : undefined

  return (
    <AuthCard unitId={unitId('a-orgblocked')}>
      <AuthCardBrand label={AUTH_BRAND} />
      <h3 className="m-0 mb-3 text-section font-semibold">{A_ORGBLOCKED_COPY.heading}</h3>
      <AuthCardLead>{A_ORGBLOCKED_COPY.leadTemplate(orgName)}</AuthCardLead>

      <Alert variant="danger">{A_ORGBLOCKED_COPY.errorCodeLabel}</Alert>
      <Note>{A_ORGBLOCKED_COPY.nothingDeletedNote}</Note>

      {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : null)}

      {reachable.map((org) => (
        <div key={org.org_id} className="mt-3">
          <Button data-act={actionId('nav.a-orgblocked')} full onClick={onOrgSwitched}>
            {A_ORGBLOCKED_COPY.switchButton}: {org.name}
          </Button>
        </div>
      ))}

      <div className="mt-9">
        <Button data-act={undefined} disabled>
          {A_ORGBLOCKED_COPY.contactSupportButton}
        </Button>
      </div>

      <Note>{A_ORGBLOCKED_COPY.notADeadEndNote}</Note>

      <p className="mt-14">
        <a
          href="#"
          data-act={actionId('session.signOut')}
          onClick={(e) => { e.preventDefault(); signOut.mutate() }}
          className="text-link text-small"
        >
          {A_ORGBLOCKED_COPY.signOutInsteadLabel}
        </a>
      </p>
    </AuthCard>
  )
}
